import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Alert,
} from '@mui/material';
import { Email, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hook';
import {
  login,
  fetchProfile,
  selectAuthError,
  selectAuthLoading,
  selectIsAuthenticated,
  clearError,
} from '../redux/slices/auth.slice';
import BackgroundSVG from '../components/common/BackgroundSVG';
import LlamaLogo from '../components/common/LLamaLogo';
import PasswordVisibilityAdornment from '../components/common/PasswordVisibilityAdornment';
import { HOME_PATH } from '../constants/routes.constants';
import { loginBrand, loginFieldSx, loginLabelSx, loginPanelSx } from './loginPage.styles';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const error = useAppSelector(selectAuthError);
  const isLoading = useAppSelector(selectAuthLoading);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const mustChangePassword = useAppSelector((state) => state.auth.mustChangePassword);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (mustChangePassword) {
      navigate('/force-change-password', { replace: true });
      return;
    }
    navigate(HOME_PATH, { replace: true });
  }, [isAuthenticated, mustChangePassword, navigate]);

  useEffect(() => {
    dispatch(clearError());
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberMe');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const resultAction = await dispatch(login({ email, password }));

    if (login.fulfilled.match(resultAction)) {
      if (rememberMe) {
        localStorage.setItem('rememberMe', email);
      } else {
        localStorage.removeItem('rememberMe');
      }

      if (resultAction.payload.mustChangePassword) {
        navigate('/force-change-password');
        return;
      }

      try {
        await dispatch(fetchProfile()).unwrap();
        navigate(HOME_PATH);
      } catch (err) {
        console.error('Failed to fetch profile after login:', err);
        navigate(HOME_PATH);
      }
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <BackgroundSVG />

      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 10, px: 2 }}>
        <Paper elevation={0} sx={loginPanelSx}>
          <Box
            sx={{
              px: { xs: 3, sm: 4 },
              pt: 4.5,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Box sx={{ mb: 1.5 }}>
              <LlamaLogo size={72} />
            </Box>

            <Typography
              component="h1"
              sx={{
                fontFamily: loginBrand.display,
                fontWeight: 600,
                fontSize: { xs: '2rem', sm: '2.35rem' },
                letterSpacing: '0.08em',
                lineHeight: 1.15,
                color: loginBrand.ink,
              }}
            >
              LLama
              <Box component="span" sx={{ color: loginBrand.accent }}>
                One
              </Box>
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              px: { xs: 3, sm: 4 },
              pt: 2.5,
              pb: 3.5,
            }}
          >
            <Typography component="label" htmlFor="email" sx={loginLabelSx}>
              Correo electrónico
            </Typography>
            <TextField
              margin="none"
              required
              fullWidth
              id="email"
              name="email"
              autoComplete="email"
              autoFocus
              placeholder="usuario@llamatech.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email fontSize="small" sx={{ color: loginBrand.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={loginFieldSx}
            />

            <Typography component="label" htmlFor="password" sx={loginLabelSx}>
              Contraseña
            </Typography>
            <TextField
              margin="none"
              required
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock fontSize="small" sx={{ color: loginBrand.muted }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <PasswordVisibilityAdornment
                    visible={showPassword}
                    onVisibleChange={setShowPassword}
                    sx={{
                      color: loginBrand.muted,
                      '&:hover': { color: loginBrand.mutedStrong },
                    }}
                  />
                ),
              }}
              sx={{ ...loginFieldSx, mb: error ? 1 : 2.5 }}
            />

            {error && (
              <Alert
                severity="error"
                variant="outlined"
                sx={{
                  mb: 1.5,
                  border: 'none',
                  bgcolor: 'transparent',
                  color: loginBrand.error,
                  fontWeight: 600,
                  fontFamily: loginBrand.body,
                  py: 0,
                  '& .MuiAlert-message': { fontWeight: 600 },
                  '& .MuiAlert-icon': { color: loginBrand.error },
                }}
              >
                {error}
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 0.5,
                mb: 3,
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      color: loginBrand.checkboxIdle,
                      '&.Mui-checked': { color: loginBrand.accent },
                    }}
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    name="rememberMe"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: loginBrand.mutedStrong,
                      fontFamily: loginBrand.body,
                      fontSize: '0.875rem',
                    }}
                  >
                    Recordar mis datos
                  </Typography>
                }
              />
              <Typography
                variant="body2"
                sx={{
                  color: loginBrand.muted,
                  fontFamily: loginBrand.body,
                  fontSize: '0.78rem',
                }}
              >
                ¿Olvidó su contraseña? Contacte al administrador
              </Typography>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.55,
                fontSize: '0.95rem',
                fontFamily: loginBrand.body,
                fontWeight: 600,
                letterSpacing: '0.02em',
                textTransform: 'none',
                borderRadius: '12px',
                color: 'common.white',
                background: (theme) => theme.gradients.primaryCta,
                boxShadow: 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
                '&:hover': {
                  background: (theme) => theme.gradients.primaryCtaHover,
                  boxShadow: '0 8px 20px rgba(6, 182, 212, 0.28)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(to right, #a5f3fc, #a7f3d0)',
                  color: '#115E59',
                },
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                  '&:hover': { transform: 'none' },
                  '&:active': { transform: 'none' },
                },
              }}
            >
              {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </Button>

            <Typography
              sx={{
                mt: 3,
                textAlign: 'center',
                fontFamily: loginBrand.body,
                fontSize: '0.72rem',
                color: loginBrand.muted,
              }}
            >
              © {new Date().getFullYear()} Llamita Tech Inc. Todos los derechos reservados.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
